import { useRef, useState } from "react";

const fmtPrice = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
};

export default function Sparkline({ data, width = 280, height = 60, color = "#FAFAFA", divColor = "#FFD60A" }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(null); // { idx, x, y }

  if (!data || !data.prices || data.prices.length < 2) {
    return (
      <div
        className="border border-zinc-900 bg-[#0d0d0f]"
        style={{ width: "100%", height }}
      />
    );
  }

  const closes = data.prices.map((p) => p.c);
  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;
  const n = data.prices.length;

  const padX = 4;
  const padY = 6;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;

  const points = data.prices.map((p, i) => {
    const x = padX + (i / (n - 1)) * innerW;
    const y = padY + innerH - ((p.c - min) / range) * innerH;
    return [x, y, p.d, p.c];
  });

  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const areaPath = `${path} L${points[n - 1][0].toFixed(2)},${(padY + innerH).toFixed(2)} L${points[0][0].toFixed(2)},${(padY + innerH).toFixed(2)} Z`;

  const divPts = (data.dividends || []).map((d) => {
    let bestIdx = 0;
    for (let i = 0; i < n; i++) {
      if (data.prices[i].d <= d.d) bestIdx = i;
      else break;
    }
    const [x, y] = points[bestIdx];
    return { x, y, d: d.d, a: d.a };
  });

  const isUp = closes[n - 1] >= closes[0];
  const stroke = color === "#FAFAFA" ? (isUp ? "#00E676" : "#FF3B30") : color;

  const handleMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const xPct = (e.clientX - rect.left) / rect.width;
    // Map to nearest point index based on viewBox X
    const targetVbX = padX + xPct * innerW;
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = Math.abs(points[i][0] - targetVbX);
      if (d < bestDiff) {
        bestDiff = d;
        bestIdx = i;
      }
    }
    setHover({
      idx: bestIdx,
      // pixel-space for tooltip
      px: (points[bestIdx][0] / width) * rect.width,
      py: (points[bestIdx][1] / height) * rect.height,
    });
  };

  const handleLeave = () => setHover(null);

  const tooltip = hover != null ? data.prices[hover.idx] : null;

  return (
    <div ref={wrapRef} className="relative" style={{ height }} data-testid="sparkline-wrap">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="block"
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        data-testid="sparkline"
      >
        <defs>
          <linearGradient id={`spark-fill-${stroke.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#spark-fill-${stroke.replace("#", "")})`} />
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.4" />
        {divPts.map((dp) => (
          <circle
            key={`div-${dp.d}`}
            cx={dp.x}
            cy={dp.y}
            r="1.8"
            fill={divColor}
            stroke="#09090B"
            strokeWidth="0.5"
          >
            <title>
              Dividend {dp.a} on {dp.d}
            </title>
          </circle>
        ))}
        {hover && (
          <g>
            <line
              x1={points[hover.idx][0]}
              x2={points[hover.idx][0]}
              y1={padY}
              y2={padY + innerH}
              stroke="#52525B"
              strokeDasharray="2 2"
              strokeWidth="0.8"
            />
            <circle
              cx={points[hover.idx][0]}
              cy={points[hover.idx][1]}
              r="2.5"
              fill={stroke}
              stroke="#09090B"
              strokeWidth="0.8"
            />
          </g>
        )}
      </svg>
      {tooltip && hover && (
        <div
          className="absolute pointer-events-none border border-zinc-700 bg-[#0d0d0f] px-2 py-1 text-[10px] font-mono z-20"
          style={{
            left: Math.min(Math.max(hover.px - 50, 0), 999),
            top: -2,
            transform: "translateY(-100%)",
            whiteSpace: "nowrap",
          }}
          data-testid="sparkline-tooltip"
        >
          <span className="text-zinc-500">{fmtDate(tooltip.d)}</span>{" "}
          <span className="text-zinc-100">{fmtPrice(tooltip.c)}</span>
        </div>
      )}
    </div>
  );
}
